import type { FC } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierPaymentMethods } from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Bucket, TextLink } from '@eneco/sparky';
import { MyEnecoInvoiceBillingDetailsRendering } from '@sitecore/types/MyEnecoInvoiceBillingDetails';

import { BillingDetailsReadFormField } from './BillingDetailsReadFormField';
import DataErrorNotification from '../../../../../components/DataErrorNotification';
import ReadFormField from '../../../../../components/ReadFormField';
import { useAddressOptions } from '../../../../../hooks/useAddressOptions';
import { ADDRESS_PARAM } from '../../../../../utils/addSearchParams';

type InvoicePaymentMethodBucketProps = {
  fields: MyEnecoInvoiceBillingDetailsRendering['fields']['paymentMethod'];
  addressIdentifier: string | null | undefined;
  isLoadingAddress: boolean;
};

export const InvoicePaymentMethodBucket: FC<InvoicePaymentMethodBucketProps> = ({
  fields,
  addressIdentifier,
  isLoadingAddress,
}) => {
  const { isCurrentAccountReader, selectedAccount } = useSelfServiceAccount();
  const { addressOptions } = useAddressOptions() || {};
  const selectedAddress = addressOptions?.find(option => option.value === addressIdentifier);
  const { data: session } = useSession();

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierPaymentMethods,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
        addressIdentifier: selectedAddress?.value ?? '',
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/delivery-addresses/${selectedAddress?.value}/payment-methods`],
    session,
  );

  const isLoading = isLoadingPaymentMethods || isLoadingAddress;
  const fetchingPaymentMethodError = paymentMethodsError !== undefined && !paymentMethodsData;

  if (paymentMethodsData?.error?.errors?.status === 404) {
    return null;
  }

  if (!isLoading && fetchingPaymentMethodError) {
    return <DataErrorNotification />;
  }

  const data = unwrapData(paymentMethodsData);

  return (
    <Bucket title={fields.title.value}>
      <Bucket.Content>
        {isLoading && <ReadFormField isLoading={isLoading} isLoadingLabel={isLoading} value={''} label={''} />}

        {data?.items &&
          data.items.length &&
          data.items.map(billingAccount => {
            if (!billingAccount.meters) return null;
            const paymentMethodLabel = fields.methodsList.value.enum.find(
              x => x.value?.toLocaleLowerCase() === billingAccount.currentPaymentMethod?.toLocaleLowerCase(),
            )?.label;
            return (
              <BillingDetailsReadFormField
                key={billingAccount.billingAccountNumber}
                meters={billingAccount.meters}
                value={paymentMethodLabel}
              />
            );
          })}
        {!isCurrentAccountReader && (
          <TextLink
            emphasis="high"
            href={`${fields.updateLink.value.href.replace(/\/$/, '')}?${ADDRESS_PARAM}=${selectedAddress?.value}`}>
            {fields.updateLink?.value?.text}
          </TextLink>
        )}
      </Bucket.Content>
    </Bucket>
  );
};
